// services/kyc/derive.ts
//
// The single brain of the KYC system: derived types + the pure derivation. No React, no
// network, no side effects. Tested in derive.test.ts against the 21 real backend traces.
// Lives next to the KYC RTK Query service and raw types, same pattern as services/otc/engine.ts.

import type { kycBrief, KycStatus, kycRejectionReason } from './types';

// ── Derived types ────────────────────────────────────────────────────────────

export type TierStatus =
  | 'locked' // advanced only, before basic approved
  | 'not_started' // chip انجام نشده, action تکمیل
  | 'in_progress' // partially done; still shows تکمیل / ادامه
  | 'under_review' // chip در حال بررسی, action مشاهده
  | 'rejected' // chip رد شده, action اصلاح
  | 'approved'; // chip تایید شده

export type KycStep =
  | 'overview'
  | 'mobile'
  | 'personal_info' // national id + birthdate + (mandatory when isRequired) id image; also "fix basic"
  | 'basic_under_review'
  | 'advanced_identity' // also "fix identity"
  | 'advanced_selfie' // also "fix selfie"
  | 'advanced_under_review'
  | 'done';

export interface KycTier {
  status: TierStatus;
  reasons: kycRejectionReason[];
}

export interface KycCapabilities {
  canDepositToman: boolean;
  canDepositCrypto: boolean;
  canWithdrawToman: boolean;
  canWithdrawCrypto: boolean; // requires advanced
  canTrade: boolean;
}

export interface KycState {
  loaded: boolean;
  raw: kycBrief | null;
  level: 'zero' | 'one' | 'two';
  basic: KycTier;
  advanced: KycTier;
  step: KycStep;
  isBasicVerified: boolean;
  isFullyVerified: boolean;
  capabilities: KycCapabilities;
}

// ── Derivation ───────────────────────────────────────────────────────────────

const isRej = (s?: KycStatus) => s === 'KYC_STATUS_REJECTED';
const isApp = (s?: KycStatus) => s === 'KYC_STATUS_APPROVED';
const isSub = (s?: KycStatus) => s === 'KYC_STATUS_SUBMITTED';
const isUns = (s?: KycStatus) => s === 'KYC_STATUS_UNSPECIFIED';

// eslint-disable-next-line @typescript-eslint/default-param-last
const bySection = (arr: kycRejectionReason[] = [], section: string) =>
  arr.filter((r) => r.section === section);

/** Safe state for unauthenticated / not-yet-loaded. Everything locked, no capabilities. */
export const DEFAULT_KYC_STATE: KycState = {
  loaded: false,
  raw: null,
  level: 'zero',
  basic: { status: 'not_started', reasons: [] },
  advanced: { status: 'locked', reasons: [] },
  step: 'overview',
  isBasicVerified: false,
  isFullyVerified: false,
  capabilities: {
    canDepositToman: false,
    canDepositCrypto: false,
    canWithdrawToman: false,
    canWithdrawCrypto: false,
    canTrade: false
  }
};

// Basic tier (grants متوسط). kycLevel + status only. mobileStatus/basicInfoStatus are noise.
function deriveBasicTier(b: kycBrief, hasMobile: boolean): TierStatus {
  if (b.kycLevel === 'KYC_LEVEL_TWO') return 'approved'; // upper wins
  if (b.kycLevel === 'KYC_LEVEL_ONE') {
    if (isApp(b.status)) return 'approved';
    if (isRej(b.status)) return 'rejected';
    return 'under_review'; // SUBMITTED, or UNSPECIFIED (== just submitted)
  }
  // level ZERO — the brief can't see mobile submission here; token.Mobile fills the gap.
  return hasMobile ? 'in_progress' : 'not_started';
}

// Advanced tier (grants پیشرفته). status===APPROVED means fully done; else read sub-statuses.
function deriveAdvancedTier(b: kycBrief): TierStatus {
  const basicApproved =
    b.kycLevel === 'KYC_LEVEL_TWO' ||
    (b.kycLevel === 'KYC_LEVEL_ONE' && isApp(b.status));

  if (!basicApproved) return 'locked';
  if (b.kycLevel === 'KYC_LEVEL_ONE') return 'not_started'; // basic done, advanced not begun

  // level TWO
  if (isApp(b.status)) return 'approved';
  if (isRej(b.identityPicStatus) || isRej(b.selfieStatus)) return 'rejected';
  if (isSub(b.identityPicStatus) || isApp(b.identityPicStatus)) {
    // identity in, selfie pending → resume; both in → awaiting final review
    return isUns(b.selfieStatus) ? 'in_progress' : 'under_review';
  }
  return 'in_progress';
}

/** The one canonical step the user is / should be on. */
export function resolveStep(b: kycBrief, hasMobile: boolean): KycStep {
  if (b.kycLevel === 'KYC_LEVEL_ZERO') {
    return hasMobile ? 'personal_info' : 'mobile';
  }

  if (b.kycLevel === 'KYC_LEVEL_ONE') {
    if (isApp(b.status)) return 'advanced_identity'; // basic done → enter advanced
    if (isRej(b.status)) return 'personal_info'; // fix basic (L1 reasons)
    return 'basic_under_review';
  }

  // level TWO
  if (isApp(b.status)) return 'done';
  if (isRej(b.identityPicStatus)) return 'advanced_identity'; // fix identity
  if (isRej(b.selfieStatus)) return 'advanced_selfie'; // fix selfie
  if (isUns(b.identityPicStatus)) return 'advanced_identity';
  if (isUns(b.selfieStatus)) return 'advanced_selfie'; // identity in, selfie pending
  return 'advanced_under_review';
}

export function deriveKycState(
  brief: kycBrief | null | undefined,
  hasMobile: boolean
): KycState {
  if (!brief) return DEFAULT_KYC_STATE;

  const basicStatus = deriveBasicTier(brief, hasMobile);
  const advancedStatus = deriveAdvancedTier(brief);

  // A reason is LIVE only when its section's status is REJECTED (prevents stale reasons leaking).
  const basicReasons =
    brief.kycLevel === 'KYC_LEVEL_ONE' && isRej(brief.status)
      ? bySection(brief.rejectionReasonsL1, 'KYC_SECTION_BASIC_INFO')
      : [];

  const advancedReasons =
    brief.kycLevel === 'KYC_LEVEL_TWO'
      ? [
          ...(isRej(brief.identityPicStatus)
            ? bySection(brief.rejectionReasonsL2, 'KYC_SECTION_IDENTITY_CARD')
            : []),
          ...(isRej(brief.selfieStatus)
            ? bySection(brief.rejectionReasonsL2, 'KYC_SECTION_SELFIE')
            : [])
        ]
      : [];

  const level =
    brief.kycLevel === 'KYC_LEVEL_TWO'
      ? 'two'
      : brief.kycLevel === 'KYC_LEVEL_ONE'
      ? 'one'
      : 'zero';

  const isBasicVerified = basicStatus === 'approved';
  const isFullyVerified = advancedStatus === 'approved';

  return {
    loaded: true,
    raw: brief,
    level,
    basic: { status: basicStatus, reasons: basicReasons },
    advanced: { status: advancedStatus, reasons: advancedReasons },
    step: resolveStep(brief, hasMobile),
    isBasicVerified,
    isFullyVerified,
    capabilities: {
      canDepositToman: isBasicVerified,
      canDepositCrypto: isBasicVerified,
      canWithdrawToman: isBasicVerified,
      canTrade: isBasicVerified,
      canWithdrawCrypto: isFullyVerified
    }
  };
}
